/**
 * Toast Container - Global Notification Delivery System
 *
 * Elegant, glassmorphic notifications that float with precision.
 * Adheres to the "Operational Architect" aesthetic.
 */
import { useNotificationStore } from '../store/notifications';
import { AlertCircle, CheckCircle, Info, AlertTriangle, X } from 'lucide-react';
import { useLanguage } from '../lib/LanguageContext';
import { copy } from '../copy/copy';

const icons = {
  success: CheckCircle,
  error: AlertCircle,
  warning: AlertTriangle,
  info: Info,
};

const colors = {
  success: 'text-success bg-success-container/30',
  error: 'text-error bg-error/10',
  warning: 'text-secondary bg-secondary/10',
  info: 'text-primary bg-primary/10',
};

const labelKeys = {
  success: copy.toast.labelSuccess,
  error: copy.toast.labelError,
  warning: copy.toast.labelWarning,
  info: copy.toast.labelInfo,
};

export function ToastContainer() {
  const { t } = useLanguage();

  const { notifications, removeNotification } = useNotificationStore();

  return (
    <div className="fixed bottom-10 right-10 z-[100] flex flex-col gap-5 max-w-sm w-full pointer-events-none">
      {notifications.map(n => {
        const Icon = icons[n.type];
        return (
          <div
            key={n.id}
            className={`
              pointer-events-auto
              flex items-start gap-5 p-6 rounded-2xl
              bg-surface-container-highest/80 backdrop-blur-3xl
              shadow-level4 animate-slideInRight
              border-l-4 ${n.type === 'error' ? 'border-error' : n.type === 'success' ? 'border-success' : 'border-primary'}
              relative overflow-hidden
            `}
          >
            {/* Tonal Shine / Backdrop Detail */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 blur-3xl rounded-full -mr-16 -mt-16 pointer-events-none" />

            <div className={`p-3 rounded-xl shrink-0 ${colors[n.type]}`}>
              <Icon size={20} />
            </div>

            <div className="flex-1 min-w-0 pr-4">
              <h5 className="text-[10px] font-headline font-black uppercase tracking-[0.25em] text-on-surface mb-2 opacity-60">
                {n.title || t(labelKeys[n.type])}
              </h5>
              <p className="text-[11px] font-body font-bold text-on-surface leading-snug">
                {n.message}
              </p>
            </div>

            <button
              onClick={() => removeNotification(n.id)}
              className="p-1.5 hover:bg-surface-container-high rounded-xl text-on-surface-variant/40 hover:text-on-surface transition-all active:scale-90"
              aria-label={t(copy.toast.ariaDismiss)}
            >
              <X size={16} />
            </button>
          </div>
        );
      })}
    </div>
  );
}
